import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, TrendingUp, Plus, Trash2 } from "lucide-react";
import { base44 } from "@/api/base44Client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { TrackingLayout, RequestError } from "@/components/tracking/TrackingLayout";
import { TrendChart } from "@/components/tracking/TrendChart";
import { ProgressSummary } from "@/components/tracking/ProgressSummary";
import { WeightHistory } from "@/components/tracking/WeightHistory";
import { useRecords } from "@/hooks/useRecords";
import { chartRecords, dateLocale } from "@/lib/tracking";

export default function Progress() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const locale = dateLocale(i18n.language);
  const { records, setRecords, error, setError, loading } = useRecords(base44.entities.WeightRecord);
  const imc = useRecords(base44.entities.ImcRecord);
  const [weight, setWeight] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const chartData = useMemo(() => chartRecords(records, locale), [records, locale]);

  const addRecord = async () => {
    const value = Number(weight);
    if (!Number.isFinite(value) || value < 20 || value > 300) { setError(t("progress.weightRangeError")); return; }
    setBusy(true); setError("");
    try {
      const record = await base44.entities.WeightRecord.create({ weight: value, note });
      setRecords((previous) => [record, ...previous]); setWeight(""); setNote("");
    } catch (failure) { setError(failure.message); } finally { setBusy(false); }
  };
  const clearRecords = async () => {
    if (!window.confirm(t("progress.deleteHistoryConfirm"))) return;
    setBusy(true); setError("");
    try { await Promise.all(records.map((record) => base44.entities.WeightRecord.delete(record.id))); setRecords([]); }
    catch (failure) { setError(failure.message); } finally { setBusy(false); }
  };
  return <TrackingLayout title={t("progress.title")} icon={TrendingUp} testId="progress">
    <RequestError error={error || imc.error} testId="progress-error" />
    {loading && <p data-testid="progress-loading">{t("common.loading")}</p>}
    <ProgressSummary records={records} lastImc={imc.records[0]} />
    <div className="bg-card border border-border rounded-xl p-5 space-y-4">
      <h2 className="font-semibold">{t("progress.registerNew")}</h2>
      <div className="space-y-1.5"><Label htmlFor="progress-weight">{t("imc.weight")}</Label><Input id="progress-weight" data-testid="progress-weight-input" type="number" min="20" max="300" step="0.1" placeholder="Ex: 72.5" value={weight} onChange={(event) => setWeight(event.target.value)} className="bg-secondary border-border" /></div>
      <div className="space-y-1.5"><Label htmlFor="progress-note">{t("progress.note")}</Label><Input id="progress-note" data-testid="progress-note-input" placeholder={t("progress.notePlaceholder")} value={note} onChange={(event) => setNote(event.target.value)} className="bg-secondary border-border" /></div>
      <Button onClick={addRecord} disabled={busy || !weight || loading} data-testid="progress-save" className="w-full"><Plus className="w-4 h-4 mr-1" />{t("progress.register")}</Button>
    </div>
    {chartData.length > 1 && <div className="bg-card border border-border rounded-xl p-5"><h2 className="font-semibold mb-4">{t("progress.weightEvolution")}</h2><TrendChart data={chartData} testId="progress-weight-chart" /></div>}
    {records.length > 0 && <><WeightHistory records={records} locale={locale} /><Button onClick={clearRecords} disabled={busy} data-testid="progress-delete-history" variant="outline" className="w-full text-red-400 h-12"><Trash2 className="w-4 h-4 mr-2" />{t("progress.deleteHistory")}</Button></>}
    <Button onClick={() => navigate(-1)} data-testid="progress-return" variant="outline" className="w-full h-12"><ArrowLeft className="w-4 h-4 mr-2" />{t("common.back")}</Button>
  </TrackingLayout>;
}